import { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { errMsg } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function SaveBar({ onSave, testId = "save-settings-btn" }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onSave();
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button data-testid={testId} disabled={busy} onClick={run} className="rounded-full bg-emerald-400 px-8 text-emerald-950 hover:bg-emerald-300">
      {busy ? "Saving..." : "Save changes"}
    </Button>
  );
}

export default function ScheduleTab({ draft, save }) {
  const [s, setS] = useState(draft);
  const [holiday, setHoliday] = useState("");
  const setDay = (d, ranges) => setS({ ...s, weekly_hours: { ...s.weekly_hours, [d]: ranges } });

  return (
    <div className="space-y-6" data-testid="admin-schedule">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Slot length (minutes) - each booking uses as many slots as its duration</Label>
          <Input data-testid="slot-minutes-input" type="number" min={5} max={60} value={s.slot_minutes} onChange={(e) => setS({ ...s, slot_minutes: Number(e.target.value) })} className="bg-black/20" />
        </div>
        <div className="space-y-1.5">
          <Label>Patients can book up to (days ahead)</Label>
          <Input data-testid="days-ahead-input" type="number" min={1} max={180} value={s.booking_days_ahead} onChange={(e) => setS({ ...s, booking_days_ahead: Number(e.target.value) })} className="bg-black/20" />
        </div>
      </div>
      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-emerald-300/20 bg-black/15 p-4 text-sm text-emerald-50/90" data-testid="allow-skip-payment-row">
        <input
          type="checkbox"
          data-testid="allow-skip-payment-toggle"
          checked={s.allow_skip_payment !== false}
          onChange={(e) => setS({ ...s, allow_skip_payment: e.target.checked })}
          className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-emerald-400"
        />
        <span>
          <b className="text-white">Allow "Proceed without payment"</b>
          <span className="block text-xs text-emerald-100/60">
            When off, patients must complete UPI payment (QR / UPI ID with UTR or screenshot) to finish booking - the "Proceed without payment" button is hidden on the payment page.
          </span>
        </span>
      </label>
      <div className="space-y-3">
        <p className="font-display text-lg text-white">Weekly consultation hours (India time)</p>
        {DAYS.map((name, i) => {
          const ranges = s.weekly_hours[String(i)] || [];
          return (
            <div key={name} className="flex flex-wrap items-center gap-2 border-b border-white/5 pb-3" data-testid={`hours-day-${i}`}>
              <span className="w-28 text-sm">{name}</span>
              {ranges.length === 0 && <span className="text-xs text-emerald-100/50">Closed</span>}
              {ranges.map((r, j) => (
                <span key={j} className="flex items-center gap-1">
                  <Input type="time" value={r.start} onChange={(e) => setDay(i, ranges.map((x, k) => (k === j ? { ...x, start: e.target.value } : x)))} className="h-9 w-28 bg-black/20" />
                  <span>-</span>
                  <Input type="time" value={r.end} onChange={(e) => setDay(i, ranges.map((x, k) => (k === j ? { ...x, end: e.target.value } : x)))} className="h-9 w-28 bg-black/20" />
                  <Button size="icon" variant="ghost" onClick={() => setDay(i, ranges.filter((_, k) => k !== j))}><Trash2 className="h-4 w-4" /></Button>
                </span>
              ))}
              <Button size="sm" variant="ghost" data-testid={`add-range-${i}`} onClick={() => setDay(i, [...ranges, { start: "10:00", end: "12:00" }])}><Plus className="mr-1 h-4 w-4" /> Add hours</Button>
            </div>
          );
        })}
      </div>
      <div className="space-y-3">
        <p className="font-display text-lg text-white">Holidays / leave days</p>
        <div className="flex gap-2">
          <Input data-testid="holiday-input" type="date" value={holiday} onChange={(e) => setHoliday(e.target.value)} className="w-48 bg-black/20" />
          <Button variant="secondary" data-testid="add-holiday-btn" onClick={() => holiday && setS({ ...s, holidays: [...new Set([...s.holidays, holiday])].sort() })}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {s.holidays.map((h) => (
            <span key={h} className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs" data-testid={`holiday-${h}`}>
              {h}
              <button type="button" onClick={() => setS({ ...s, holidays: s.holidays.filter((x) => x !== h) })}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      </div>
      <SaveBar onSave={() => save(s)} />
    </div>
  );
}
